import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { api } from '@/api/client';
import type { BulkImportResponse } from '../types';
import type { BulkImportRequest } from '../utils/buildBulkRequest';

export { buildBulkRequest } from '../utils/buildBulkRequest';
export type { BulkImportRequest } from '../utils/buildBulkRequest';

// --- Preflight types ---

export interface PreflightDuplicateMatch {
  id: number;
  common_name: string;
  vendor: string | null;
  acquisition_date: string;
  status: string;
}

export interface PreflightDuplicate {
  index: number;
  incoming: BulkImportRequest['items'][0];
  existing: PreflightDuplicateMatch;
}

export interface PreflightResponse {
  ready_count: number;
  duplicate_count: number;
  duplicates: PreflightDuplicate[];
}

export function useBulkImport() {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkImportRequest) => {
      if (!selectedTank) throw new Error('No tank selected');
      return api.post<BulkImportResponse>(
        `/api/tanks/${selectedTank.id}/livestock/bulk`,
        data,
      );
    },
    onSuccess: () => {
      // Invalidate livestock queries so the list refreshes
      queryClient.invalidateQueries({ queryKey: ['livestock', selectedTank?.id] });
      queryClient.invalidateQueries({ queryKey: ['livestock-summary', selectedTank?.id] });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Import Failed',
        message: error.message || 'Failed to import livestock. Please try again.',
        color: 'red',
      });
    },
  });
}

export function useBulkPreflight() {
  const { selectedTank } = useTank();

  return useMutation({
    mutationFn: (data: BulkImportRequest) => {
      if (!selectedTank) throw new Error('No tank selected');
      return api.post<PreflightResponse>(
        `/api/tanks/${selectedTank.id}/livestock/bulk/preflight`,
        data,
      );
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Preflight Check Failed',
        message: error.message || 'Failed to check for duplicates. Please try again.',
        color: 'red',
      });
    },
  });
}
